import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  documentId,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "withdraw_request", label: "withdraw_request" },
  { value: "withdraw_paid", label: "withdraw_paid" },
  { value: "withdraw_revert", label: "withdraw_revert" },
  { value: "call_earning", label: "call_earning" },
  { value: "gift_earning", label: "gift_earning" },
];

const EARNING_TYPES = new Set(["call_earning", "gift_earning"]);
const WITHDRAW_TYPES = new Set([
  "withdraw_request",
  "withdraw_paid",
  "withdraw_revert",
]);

/** Ledger outflow: tokens reserved/deducted at request time only. */
function isWithdrawBalanceDebit(row) {
  return (
    row.type === "withdraw_request" &&
    String(row.direction || "debit").toLowerCase() === "debit"
  );
}

/** Paid confirmation row — not a second balance debit (legacy rows may still say debit). */
function isWithdrawPaidSettlementRow(row) {
  if (row.type !== "withdraw_paid") return false;
  const dir = String(row.direction || "").toLowerCase();
  return dir === "settlement" || dir === "none" || dir === "";
}

function dayBoundsMs(isoDate, endOfDay) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function csvEscape(value) {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatUsdPreview(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "—";
}

/** Excel-friendly UTC timestamp, e.g. 2026-05-17 06:25:14 UTC */
function formatUtcDate(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "";

  const d = new Date(n);
  const pad = (v) => String(v).padStart(2, "0");

  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
  );
}

function getRowRole(row) {
  if (isWithdrawBalanceDebit(row)) return "balance_debit";
  if (isWithdrawPaidSettlementRow(row)) return "settlement_log";
  if (row.type === "withdraw_revert") return "revert_credit";
  if (EARNING_TYPES.has(row.type)) return "earning";
  return "other";
}

function getIncludeInOutflowSum(row) {
  return isWithdrawBalanceDebit(row) ? "YES" : "NO";
}

function shouldExcludeSettlementFromCsv(row, excludeSettlement) {
  return Boolean(excludeSettlement) && isWithdrawPaidSettlementRow(row);
}

function formatUsdForCsv(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
}

function formatBoolAttestation(value, hasIdentitySnapshot) {
  if (!hasIdentitySnapshot) return "";
  return value === true ? "YES" : "NO";
}

function safeObject(raw) {
  return raw != null && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
}

/** Normalize a withdrawals doc for audit CSV (public / admin-readable fields only). */
function normalizeWithdrawalForAudit(docSnap) {
  const data = docSnap.data() || {};
  const identity = safeObject(data.identitySnapshot);
  const preview = safeObject(data.payoutPreview);
  const conversion = safeObject(data.conversionSnapshot);
  const hasIdentitySnapshot = Object.keys(identity).length > 0;

  return {
    id: docSnap.id,
    createdAtMs: Number(data.createdAtMs || 0),
    paidAtMs: Number(data.paidAtMs || 0),
    uid: String(data.uid || "").trim(),
    status: String(data.status || "").trim(),
    method: String(data.method || "").trim().toLowerCase(),
    amountTokens: Number(data.amountTokens || 0),
    requestedUsdPreview: Number(data.requestedUsdPreview ?? 0),
    tokensPerUsd: Number(conversion.tokensPerUsd || 0),
    paymentReference: String(data.paymentReference || "").trim(),
    paidBy: String(data.paidBy || "").trim(),
    payoutCountry: String(identity.payoutCountry || "")
      .trim()
      .toUpperCase(),
    firstNameMasked: String(identity.firstNameMasked || "").trim(),
    lastNameMasked: String(identity.lastNameMasked || "").trim(),
    verificationStatus: String(identity.verificationStatus || "")
      .trim()
      .toLowerCase(),
    verificationRequestId: String(identity.verificationRequestId || "").trim(),
    payoutOwnerConfirmed: identity.payoutOwnerConfirmed === true,
    cryptoIrreversibleAcknowledged:
      identity.cryptoIrreversibleAcknowledged === true,
    hasIdentitySnapshot,
    walletMasked: String(preview.walletAddressMasked || "").trim(),
    ibanMasked: String(preview.ibanMasked || "").trim(),
    paypalMasked: String(preview.paypalEmailMasked || "").trim(),
  };
}

const WITHDRAWAL_AUDIT_CSV_HEADER = [
  "createdAtUtc",
  "paidAtUtc",
  "withdrawId",
  "uid",
  "status",
  "method",
  "amountTokens",
  "requestedUsdPreview",
  "tokensPerUsd",
  "paymentReference",
  "payoutCountry",
  "firstNameMasked",
  "lastNameMasked",
  "verificationStatus",
  "verificationRequestId",
  "payoutOwnerConfirmed",
  "cryptoIrreversibleAcknowledged",
  "paidBy",
  "walletMasked",
  "ibanMasked",
  "paypalMasked",
].join(",");

const CARD_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: 12,
};

const SUMMARY_CARD = {
  border: "1px solid #253654",
  borderRadius: 12,
  background: "linear-gradient(180deg, #12213a, #101b30)",
  padding: 14,
};

export default function AccountingReportsPage() {
  const { t } = useTranslation();
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultFromIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const [fromDate, setFromDate] = useState(defaultFromIso);
  const [toDate, setToDate] = useState(todayIso);
  const [typeFilter, setTypeFilter] = useState("all");

  const [rows, setRows] = useState([]);
  const [withdrawalsInRange, setWithdrawalsInRange] = useState([]);
  const [withdrawStatusById, setWithdrawStatusById] = useState({});
  const [pendingWithdrawalsCount, setPendingWithdrawalsCount] = useState(0);
  const [paidWithdrawTokensInRange, setPaidWithdrawTokensInRange] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [excludeSettlementRows, setExcludeSettlementRows] = useState(true);

  const fromMs = dayBoundsMs(fromDate, false);
  const toMs = dayBoundsMs(toDate, true);

  const loadData = useCallback(async () => {
    if (fromMs == null || toMs == null || fromMs > toMs) {
      setError(t("admin.finance.invalid_date_range", { defaultValue: "Invalid date range." }));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const txConstraints = [
        where("createdAtMs", ">=", fromMs),
        where("createdAtMs", "<=", toMs),
      ];
      if (typeFilter !== "all") {
        txConstraints.push(where("type", "==", typeFilter));
      }
      txConstraints.push(orderBy("createdAtMs", "desc"));

      const txSnap = await getDocs(
        query(collection(db, "walletTransactions"), ...txConstraints)
      );

      const txRows = [];
      txSnap.forEach((docSnap) => {
        const d = docSnap.data() || {};
        const ms = Number(d.createdAtMs || 0);
        txRows.push({
          id: docSnap.id,
          dateMs: ms,
          uid: String(d.uid || ""),
          type: String(d.type || ""),
          amountTokens: Number(d.amountTokens || 0),
          amountUsdPreview: Number(d.amountUsdPreview ?? 0),
          relatedId: d.relatedId != null ? String(d.relatedId) : "",
          direction: String(d.direction || ""),
        });
      });

      const relatedWithdrawIds = [
        ...new Set(
          txRows
            .filter((r) => r.relatedId && WITHDRAW_TYPES.has(r.type))
            .map((r) => r.relatedId)
        ),
      ];

      const statusMap = {};
      for (const part of chunk(relatedWithdrawIds, 10)) {
        if (part.length === 0) continue;
        const qs = query(
          collection(db, "withdrawals"),
          where(documentId(), "in", part)
        );
        const ws = await getDocs(qs);
        ws.forEach((docSnap) => {
          statusMap[docSnap.id] = String(
            (docSnap.data() || {}).status || ""
          ).trim();
        });
      }

      const wdSnap = await getDocs(
        query(
          collection(db, "withdrawals"),
          where("createdAtMs", ">=", fromMs),
          where("createdAtMs", "<=", toMs),
          orderBy("createdAtMs", "desc")
        )
      );

      let pending = 0;
      let paidTokensSum = 0;
      const auditWithdrawals = [];
      wdSnap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const st = String(data.status || "").trim().toLowerCase();
        if (["pending", "under_review", "approved"].includes(st)) {
          pending += 1;
        }
        if (st === "paid") {
          paidTokensSum += Number(data.amountTokens || 0);
        }
        auditWithdrawals.push(normalizeWithdrawalForAudit(docSnap));
      });

      setRows(txRows);
      setWithdrawalsInRange(auditWithdrawals);
      setWithdrawStatusById(statusMap);
      setPendingWithdrawalsCount(pending);
      setPaidWithdrawTokensInRange(paidTokensSum);
    } catch (e) {
      console.error("AccountingReportsPage load error:", e);
      setError(
        e?.message ||
          t("admin.finance.load_accounting_failed", { defaultValue: "Failed to load data. You may need Firestore composite indexes for walletTransactions / withdrawals date queries." })
      );
      setRows([]);
      setWithdrawalsInRange([]);
      setWithdrawStatusById({});
      setPendingWithdrawalsCount(0);
      setPaidWithdrawTokensInRange(0);
    } finally {
      setLoading(false);
    }
  }, [fromMs, toMs, typeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = useMemo(() => {
    let totalEarnings = 0;
    let totalWithdrawn = 0;
    let settlementLogCount = 0;

    for (const r of rows) {
      if (EARNING_TYPES.has(r.type) && r.direction === "credit") {
        totalEarnings += r.amountTokens;
      }
      if (isWithdrawBalanceDebit(r)) {
        totalWithdrawn += r.amountTokens;
      }
      if (isWithdrawPaidSettlementRow(r)) {
        settlementLogCount += 1;
      }
      // Legacy withdraw_paid + direction debit: do not add to outflow totals.
    }

    return {
      totalEarnings,
      totalWithdrawn,
      totalPaid: paidWithdrawTokensInRange,
      settlementLogCount,
    };
  }, [rows, paidWithdrawTokensInRange]);

  const tableRows = useMemo(() => {
    return rows.map((r) => {
      const status = WITHDRAW_TYPES.has(r.type) && r.relatedId
        ? withdrawStatusById[r.relatedId] || "—"
        : "—";
      return { ...r, status };
    });
  }, [rows, withdrawStatusById]);

  const csvExportRows = useMemo(() => {
    return tableRows.filter(
      (r) => !shouldExcludeSettlementFromCsv(r, excludeSettlementRows)
    );
  }, [tableRows, excludeSettlementRows]);

  function buildCsv(exportRows) {
    const header =
      "eventAtUtc,uid,type,direction,rowRole,includeInOutflowSum,tokens,usd,status,withdrawId";
    const lines = [header];

    for (const r of exportRows) {
      const withdrawId =
        WITHDRAW_TYPES.has(r.type) && r.relatedId ? r.relatedId : "";
      const direction = String(r.direction || "").trim().toLowerCase() || "—";

      lines.push(
        [
          csvEscape(formatUtcDate(r.dateMs)),
          csvEscape(r.uid),
          csvEscape(r.type),
          csvEscape(direction),
          csvEscape(getRowRole(r)),
          csvEscape(getIncludeInOutflowSum(r)),
          csvEscape(r.amountTokens),
          csvEscape(formatUsdPreview(r.amountUsdPreview)),
          csvEscape(r.status),
          csvEscape(withdrawId),
        ].join(",")
      );
    }

    return lines.join("\r\n");
  }

  function handleExportCsv() {
    const csv = `\uFEFF${buildCsv(csvExportRows)}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `livora-accounting-${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function buildWithdrawalAuditCsv(exportWithdrawals) {
    const lines = [WITHDRAWAL_AUDIT_CSV_HEADER];

    for (const w of exportWithdrawals) {
      lines.push(
        [
          csvEscape(formatUtcDate(w.createdAtMs)),
          csvEscape(formatUtcDate(w.paidAtMs)),
          csvEscape(w.id),
          csvEscape(w.uid),
          csvEscape(w.status),
          csvEscape(w.method),
          csvEscape(w.amountTokens),
          csvEscape(formatUsdForCsv(w.requestedUsdPreview)),
          csvEscape(
            Number.isFinite(w.tokensPerUsd) && w.tokensPerUsd > 0
              ? w.tokensPerUsd
              : ""
          ),
          csvEscape(w.paymentReference),
          csvEscape(w.payoutCountry),
          csvEscape(w.firstNameMasked),
          csvEscape(w.lastNameMasked),
          csvEscape(w.verificationStatus),
          csvEscape(w.verificationRequestId),
          csvEscape(
            formatBoolAttestation(w.payoutOwnerConfirmed, w.hasIdentitySnapshot)
          ),
          csvEscape(
            formatBoolAttestation(
              w.cryptoIrreversibleAcknowledged,
              w.hasIdentitySnapshot
            )
          ),
          csvEscape(w.paidBy),
          csvEscape(w.walletMasked),
          csvEscape(w.ibanMasked),
          csvEscape(w.paypalMasked),
        ].join(",")
      );
    }

    return lines.join("\r\n");
  }

  function handleExportWithdrawalAuditCsv() {
    const csv = `\uFEFF${buildWithdrawalAuditCsv(withdrawalsInRange)}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `livora-withdrawal-audit-${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const nf = new Intl.NumberFormat();

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">{t("admin.finance.accounting_reports", { defaultValue: "Accounting reports" })}</h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.finance.accounting_subtitle", { defaultValue: "Read-only overview of wallet ledger activity and withdrawal context for the selected period." })}
        </p>
      </header>

      <section
        className="admin1-card"
        style={{
          marginBottom: 16,
          padding: 16,
          border: "1px solid #253654",
          borderRadius: 12,
          background: "linear-gradient(180deg, #12213a, #101b30)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "flex-end",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#9fb2d8" }}>{t("common.from", { defaultValue: "From" })}</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{
                background: "#0f1a2f",
                border: "1px solid #253654",
                color: "#e8eefc",
                borderRadius: 8,
                padding: "8px 10px",
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#9fb2d8" }}>{t("common.to", { defaultValue: "To" })}</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{
                background: "#0f1a2f",
                border: "1px solid #253654",
                color: "#e8eefc",
                borderRadius: 8,
                padding: "8px 10px",
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#9fb2d8" }}>{t("common.type", { defaultValue: "Type" })}</span>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                minWidth: 200,
                background: "#0f1a2f",
                border: "1px solid #253654",
                color: "#e8eefc",
                borderRadius: 8,
                padding: "8px 10px",
              }}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={loadData}
            disabled={loading}
            style={{
              padding: "9px 14px",
              borderRadius: 8,
              border: "1px solid #37538f",
              background: "#182946",
              color: "#f3f6ff",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            Refresh
          </button>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "#c8d6f0",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={excludeSettlementRows}
              onChange={(e) => setExcludeSettlementRows(e.target.checked)}
              disabled={loading}
            />
            {t("admin.finance.exclude_settlement_rows", {
              defaultValue: "Exclude settlement rows",
            })}
          </label>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={loading || csvExportRows.length === 0}
            style={{
              padding: "9px 14px",
              borderRadius: 8,
              border: "1px solid #065f46",
              background: "#064e3b",
              color: "#a7f3d0",
              fontWeight: 600,
              cursor:
                loading || csvExportRows.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={handleExportWithdrawalAuditCsv}
            disabled={loading || withdrawalsInRange.length === 0}
            style={{
              padding: "9px 14px",
              borderRadius: 8,
              border: "1px solid #5b21b6",
              background: "#4c1d95",
              color: "#e9d5ff",
              fontWeight: 600,
              cursor:
                loading || withdrawalsInRange.length === 0
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {t("admin.finance.export_withdrawal_audit_csv", {
              defaultValue: "Export Withdrawal Audit CSV",
            })}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            style={{
              padding: "9px 14px",
              borderRadius: 8,
              border: "1px solid #334155",
              background: "#1e293b",
              color: "#e2e8f0",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Print
          </button>
        </div>
        {error ? (
          <p style={{ marginTop: 12, color: "#fca5a5", fontSize: 13 }}>
            {error}
          </p>
        ) : null}
      </section>

      <section style={CARD_GRID}>
        <div style={SUMMARY_CARD}>
          <div style={{ fontSize: 12, color: "#9fb2d8", marginBottom: 6 }}>
            Total earnings (tokens)
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#86efac" }}>
            {nf.format(summary.totalEarnings)}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            Credits: call_earning, gift_earning
          </div>
        </div>
        <div style={SUMMARY_CARD}>
          <div style={{ fontSize: 12, color: "#9fb2d8", marginBottom: 6 }}>
            Total withdrawn (tokens)
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#fcd34d" }}>
            {nf.format(summary.totalWithdrawn)}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            Sum of withdraw_request debit rows (balance reserved)
          </div>
        </div>
        <div style={SUMMARY_CARD}>
          <div style={{ fontSize: 12, color: "#9fb2d8", marginBottom: 6 }}>
            Total paid (tokens)
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#93c5fd" }}>
            {nf.format(summary.totalPaid)}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            withdrawals.status paid in range (not ledger debits)
          </div>
        </div>
        <div style={SUMMARY_CARD}>
          <div style={{ fontSize: 12, color: "#9fb2d8", marginBottom: 6 }}>
            Pending withdrawals (count)
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#fbbf24" }}>
            {nf.format(pendingWithdrawalsCount)}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
            pending / under_review / approved in range
          </div>
        </div>
      </section>

      <section
        style={{
          marginTop: 16,
          border: "1px solid #253654",
          borderRadius: 12,
          background: "#0f172a",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            borderBottom: "1px solid #1e293b",
            fontWeight: 600,
            color: "#f3f6ff",
          }}
        >
          Ledger ({loading ? "…" : tableRows.length} rows)
        </div>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              color: "#e2e8f0",
            }}
          >
            <thead>
              <tr style={{ background: "#111d33", textAlign: "left" }}>
                <th style={{ padding: "10px 12px", color: "#9fb2d8" }}>{t("common.date", { defaultValue: "Date" })}</th>
                <th style={{ padding: "10px 12px", color: "#9fb2d8" }}>{t("admin.fields.uid", { defaultValue: "uid" })}</th>
                <th style={{ padding: "10px 12px", color: "#9fb2d8" }}>{t("common.type", { defaultValue: "type" })}</th>
                <th style={{ padding: "10px 12px", color: "#9fb2d8" }}>
                  amountTokens
                </th>
                <th style={{ padding: "10px 12px", color: "#9fb2d8" }}>
                  amountUsdPreview
                </th>
                <th style={{ padding: "10px 12px", color: "#9fb2d8" }}>
                  relatedId
                </th>
                <th style={{ padding: "10px 12px", color: "#9fb2d8" }}>{t("common.status", { defaultValue: "status" })}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, color: "#94a3b8" }}>
                    Loading…
                  </td>
                </tr>
              ) : tableRows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, color: "#94a3b8" }}>
                    {t("admin.finance.no_transactions", { defaultValue: "No transactions in this range." })}
                  </td>
                </tr>
              ) : (
                tableRows.map((r) => (
                  <tr
                    key={r.id}
                    style={{ borderTop: "1px solid #1e293b" }}
                  >
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                      {r.dateMs > 0
                        ? new Date(r.dateMs).toLocaleString()
                        : "—"}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        fontFamily: "Consolas, monospace",
                        fontSize: 11,
                        wordBreak: "break-all",
                        maxWidth: 120,
                      }}
                    >
                      {r.uid || "—"}
                    </td>
                    <td style={{ padding: "8px 12px" }}>{r.type}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {nf.format(r.amountTokens)}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {formatUsdPreview(r.amountUsdPreview)}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        fontFamily: "Consolas, monospace",
                        fontSize: 11,
                        wordBreak: "break-all",
                      }}
                    >
                      {r.relatedId || "—"}
                    </td>
                    <td style={{ padding: "8px 12px" }}>{r.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <style>{`
        @media print {
          .admin1-sidebar, button { display: none !important; }
          .admin1-main { padding: 12px !important; }
        }
      `}</style>
    </div>
  );
}
